import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { WebThemeMockup } from "@/components/web-theme-mockup";
import { LaptopFrame, PhoneFrame } from "@/components/device-frames";
import { LoginBackgroundGallery, LoginScreenMockup } from "@/components/login-screen-mockup";
import { DEFAULT_LOGIN_BACKGROUND_THEME } from "@/lib/login-background-themes";
import {
  useAdminBranding,
  useUpdateAdminBranding,
  useUploadBrandingAppIcon,
  useUploadBrandingFavicon,
  useUploadBrandingLoginBackgroundImage,
  useUploadBrandingLoginBackgroundTabletImage,
  useUploadBrandingLoginBackgroundPwaImage,
  useUploadBrandingLoginLogo,
  useUploadBrandingLogo,
  useUploadBrandingLogoDark,
} from "@/hooks/queries";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import logoAsset from "@/assets/gps-drive-logo-v3.png.asset.json";
import type { AdminBrandingSettings, UploadBrandingImageResult } from "@/services/gpsdrive-admin-branding.service";
import type { UseMutationResult } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/identity")({
  component: () => (
    <AdminAccessGate action="identidade_visual">
      <IdentityPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Identidade Visual — Administração — Wp Rastreamento" }] }),
});

const PRESET_COLORS = [
  { label: "Azul", value: "#2563eb" },
  { label: "Verde", value: "#16a34a" },
  { label: "Roxo", value: "#7c3aed" },
  { label: "Laranja", value: "#ea580c" },
  { label: "Vermelho", value: "#dc2626" },
  { label: "Ciano", value: "#0891b2" },
];

// Tamanho padrão de cada logotipo quando a empresa nunca ajustou (mesma
// altura que app-sidebar.tsx/login.tsx já usavam fixo antes de virar
// configurável, ver seus respectivos className/h-* originais).
const DEFAULT_LOGO_SIZE = 128;
const DEFAULT_LOGIN_LOGO_SIZE = 96;
// Altura em px do logotipo no topo da tela Início do app/PWA (pedido
// 2026-09-01: "coloca para poder corrigir a logotipo no pwa ai consegue
// ajustar o tamanho que fica bom") — reaproveita o campo `appIconSize` que
// já existia no backend (App & SSL) mas nunca tinha UI nem consumidor no
// frontend; ver src/components/mobile-app.tsx onde é lido de verdade.
const DEFAULT_APP_LOGO_SIZE = 48;
// APP_LOGO_MAX é só o teto do slider aqui — em mobile-app.tsx (a tela real)
// o <img> do logotipo virou `absolute` (fora do fluxo), então ele pode
// crescer até esse valor sem empurrar nada nem esticar a barra (pedido
// 2026-09-01: "o fundo branco nunca pode mexer... bota pra poder aumentar
// a logotipo em até 180px").
const APP_LOGO_MIN = 24;
const APP_LOGO_MAX = 180;

interface FormState {
  themeMode: "light" | "dark";
  primaryColor: string;
  loginBackgroundColor: string;
  loginTextColor: string;
  loginBackgroundTheme: string;
  logoSize: number;
  loginLogoSize: number;
  appIconSize: number;
  serverName: string;
  serverDescription: string;
  requireClientContractSignature: boolean;
  updateNotificationEnabled: boolean;
  appleStoreUrl: string;
  googlePlayUrl: string;
  appManualUrl: string;
  termsOfUseUrl: string;
  privacyPolicyUrl: string;
  instagramUrl: string;
  facebookUrl: string;
  twitterUrl: string;
  emergencyPhone: string;
}

function toFormState(b: AdminBrandingSettings | undefined): FormState {
  return {
    themeMode: b?.themeMode ?? "light",
    primaryColor: b?.primaryColor ?? "#2563eb",
    loginBackgroundColor: b?.loginBackgroundColor ?? "#0f172a",
    loginTextColor: b?.loginTextColor ?? "#ffffff",
    loginBackgroundTheme: b?.loginBackgroundTheme ?? DEFAULT_LOGIN_BACKGROUND_THEME,
    logoSize: b?.logoSize ?? DEFAULT_LOGO_SIZE,
    loginLogoSize: b?.loginLogoSize ?? DEFAULT_LOGIN_LOGO_SIZE,
    appIconSize: b?.appIconSize ?? DEFAULT_APP_LOGO_SIZE,
    serverName: b?.serverName ?? "",
    serverDescription: b?.serverDescription ?? "",
    requireClientContractSignature: b?.requireClientContractSignature ?? false,
    updateNotificationEnabled: b?.updateNotificationEnabled ?? true,
    appleStoreUrl: b?.appleStoreUrl ?? "",
    googlePlayUrl: b?.googlePlayUrl ?? "",
    appManualUrl: b?.appManualUrl ?? "",
    termsOfUseUrl: b?.termsOfUseUrl ?? "",
    privacyPolicyUrl: b?.privacyPolicyUrl ?? "",
    instagramUrl: b?.instagramUrl ?? "",
    facebookUrl: b?.facebookUrl ?? "",
    twitterUrl: b?.twitterUrl ?? "",
    emergencyPhone: b?.emergencyPhone ?? "",
  };
}

function IdentityPage() {
  const { data: branding, isLoading } = useAdminBranding();
  const update = useUpdateAdminBranding();
  const uploadLogo = useUploadBrandingLogo();
  const uploadLogoDark = useUploadBrandingLogoDark();
  const uploadFavicon = useUploadBrandingFavicon();
  const uploadAppIcon = useUploadBrandingAppIcon();
  const uploadLoginLogo = useUploadBrandingLoginLogo();
  const uploadLoginBackgroundImage = useUploadBrandingLoginBackgroundImage();
  const uploadLoginBackgroundTabletImage = useUploadBrandingLoginBackgroundTabletImage();
  const uploadLoginBackgroundPwaImage = useUploadBrandingLoginBackgroundPwaImage();

  const [form, setForm] = useState<FormState>(toFormState(undefined));
  const [previewMode, setPreviewMode] = useState<"web" | "app">("web");

  useEffect(() => {
    if (branding) setForm(toFormState(branding));
  }, [branding]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        themeMode: form.themeMode,
        primaryColor: form.primaryColor,
        loginBackgroundColor: form.loginBackgroundColor,
        loginTextColor: form.loginTextColor,
        loginBackgroundTheme: form.loginBackgroundTheme,
        logoSize: form.logoSize,
        loginLogoSize: form.loginLogoSize,
        appIconSize: form.appIconSize,
        serverName: form.serverName || null,
        serverDescription: form.serverDescription || null,
        requireClientContractSignature: form.requireClientContractSignature,
        updateNotificationEnabled: form.updateNotificationEnabled,
        appleStoreUrl: form.appleStoreUrl || null,
        googlePlayUrl: form.googlePlayUrl || null,
        appManualUrl: form.appManualUrl || null,
        termsOfUseUrl: form.termsOfUseUrl || null,
        privacyPolicyUrl: form.privacyPolicyUrl || null,
        instagramUrl: form.instagramUrl || null,
        facebookUrl: form.facebookUrl || null,
        twitterUrl: form.twitterUrl || null,
        emergencyPhone: form.emergencyPhone || null,
      });
      toast.success("Identidade visual atualizada");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar a identidade visual");
    }
  };

  const logoUrl = branding?.logoUrl ? resolveGpsDriveUrl(branding.logoUrl) : logoAsset.url;
  const backgroundImageUrl = branding?.loginBackgroundImageUrl
    ? resolveGpsDriveUrl(branding.loginBackgroundImageUrl)
    : null;
  const dark = form.themeMode === "dark";
  // Prévia mostra o logotipo que vai aparecer de verdade no tema escolhido.
  const previewLogoUrl = dark && branding?.logoDarkUrl ? resolveGpsDriveUrl(branding.logoDarkUrl) : logoUrl;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Identidade Visual</h1>
        <p className="text-sm text-muted-foreground">
          Tema, cor, tela de login, logotipos e dados de contato — tudo em um só lugar.
        </p>
      </div>

      <GpsDriveSessionBanner />

      {/* Endereço da casa (2026-10-29): aqui se edita a identidade da GPS
          Drive, separada da empresa — a da empresa só pelo domínio dela. */}
      {branding?.house && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
          Você está editando a identidade de <strong>{window.location.hostname}</strong> (GPS Drive). Ela é separada da
          sua empresa — para mudar a identidade da sua empresa, entre pelo endereço próprio dela.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tema & Cor</CardTitle>
              <CardDescription>Modo de exibição e cor principal da plataforma.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={form.themeMode}
                onValueChange={(v) => set("themeMode", v as "light" | "dark")}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="light" id="theme-light" />
                  <Label htmlFor="theme-light">Claro</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="dark" id="theme-dark" />
                  <Label htmlFor="theme-dark">Escuro</Label>
                </div>
              </RadioGroup>

              <Separator />

              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.primaryColor}
                  onChange={(e) => set("primaryColor", e.target.value)}
                  disabled={isLoading}
                  className="h-10 w-14 cursor-pointer rounded border border-input bg-transparent p-1"
                  aria-label="Cor principal"
                />
                <span className="font-mono text-sm text-muted-foreground">{form.primaryColor}</span>
              </div>
              <div className="space-y-2">
                <Label>Paleta de cores</Label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      title={preset.label}
                      onClick={() => set("primaryColor", preset.value)}
                      className="h-8 w-8 rounded-full border-2 border-border transition-transform hover:scale-110"
                      style={{ backgroundColor: preset.value }}
                      aria-label={preset.label}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tela de Login</CardTitle>
              <CardDescription>Cor de fundo, do texto e o plano de fundo da página de login.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="login-bg">Cor de fundo</Label>
                  <div className="flex items-center gap-3">
                    <input
                      id="login-bg"
                      type="color"
                      value={form.loginBackgroundColor}
                      onChange={(e) => set("loginBackgroundColor", e.target.value)}
                      disabled={isLoading}
                      className="h-10 w-14 cursor-pointer rounded border border-input bg-transparent p-1"
                    />
                    <span className="font-mono text-sm text-muted-foreground">{form.loginBackgroundColor}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-text">Cor do texto</Label>
                  <div className="flex items-center gap-3">
                    <input
                      id="login-text"
                      type="color"
                      value={form.loginTextColor}
                      onChange={(e) => set("loginTextColor", e.target.value)}
                      disabled={isLoading}
                      className="h-10 w-14 cursor-pointer rounded border border-input bg-transparent p-1"
                    />
                    <span className="font-mono text-sm text-muted-foreground">{form.loginTextColor}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Plano de fundo</Label>
                <LoginBackgroundGallery
                  color={form.loginBackgroundColor}
                  selected={form.loginBackgroundTheme}
                  onSelect={(key) => set("loginBackgroundTheme", key)}
                  customImageUrl={backgroundImageUrl}
                />
                <p className="text-xs text-muted-foreground">✨ = fundo animado.</p>
              </div>

              {form.loginBackgroundTheme === "custom-image" && (
                <div className="grid gap-4 sm:grid-cols-3">
                  <LogoSlot
                    title="Fundo — Computador"
                    description="Imagem larga (paisagem), ex.: 1920×1080. Também é usada onde não houver imagem de tablet/app."
                    currentUrl={branding?.loginBackgroundImageUrl}
                    mutation={uploadLoginBackgroundImage}
                    fill
                  />
                  <LogoSlot
                    title="Fundo — Tablet"
                    description="Ex.: 1536×2048 (retrato) ou 2048×1536. Telas de 600 a 1024 px."
                    currentUrl={branding?.loginBackgroundTabletImageUrl}
                    mutation={uploadLoginBackgroundTabletImage}
                    fill
                  />
                  <LogoSlot
                    title="Fundo — PWA / App"
                    description="Imagem em pé (retrato), ex.: 1080×1920. Usada no app instalado e no celular."
                    currentUrl={branding?.loginBackgroundPwaImageUrl}
                    mutation={uploadLoginBackgroundPwaImage}
                    fill
                  />
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <Label>Pré-visualização da tela de login</Label>
                <LoginScreenMockup
                  backgroundColor={form.loginBackgroundColor}
                  textColor={form.loginTextColor}
                  backgroundTheme={form.loginBackgroundTheme}
                  backgroundImageUrl={backgroundImageUrl}
                  logoUrl={logoUrl}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Logotipos</CardTitle>
              <CardDescription>Cada imagem é salva assim que enviada.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <LogoSlot
                title="Logotipo do topo"
                description="Topo da plataforma (sidebar)."
                currentUrl={branding?.logoUrl}
                mutation={uploadLogo}
                size={{ value: form.logoSize, min: 32, max: 240, onChange: (v) => set("logoSize", v) }}
              />
              <LogoSlot
                title="Logotipo para fundo escuro"
                description="Usado no lugar do logotipo do topo quando o tema está Escuro (fundo preto). Sem ele, usa o normal."
                currentUrl={branding?.logoDarkUrl}
                mutation={uploadLogoDark}
                darkPreview
              />
              <LogoSlot
                title="Favicon"
                description="Ícone na aba do navegador — tamanho é decidido pelo navegador, não é ajustável aqui."
                currentUrl={branding?.faviconUrl}
                mutation={uploadFavicon}
              />
              <LogoSlot
                title="Ícone do app no celular"
                description="Aparece na tela inicial de quem instalar pelo navegador (PWA). Use imagem quadrada; ela fica em 512×512 com fundo branco."
                currentUrl={branding?.appIconUrl}
                mutation={uploadAppIcon}
              />
              <LogoSlot
                title="Logotipo do login"
                description="Página de login."
                currentUrl={branding?.loginLogoUrl}
                mutation={uploadLoginLogo}
                size={{ value: form.loginLogoSize, min: 32, max: 240, onChange: (v) => set("loginLogoSize", v) }}
              />
              <AppLogoSizeSlot
                logoUrl={branding?.logoUrl ? resolveGpsDriveUrl(branding.logoUrl) : logoAsset.url}
                value={form.appIconSize}
                min={APP_LOGO_MIN}
                max={APP_LOGO_MAX}
                onChange={(v) => set("appIconSize", v)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Dados & Contato</CardTitle>
              <CardDescription>Informações institucionais, links e canais de contato.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="server-name">Nome do servidor/empresa</Label>
                <Input id="server-name" value={form.serverName} onChange={(e) => set("serverName", e.target.value)} disabled={isLoading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="server-desc">Descrição do servidor</Label>
                <Textarea id="server-desc" value={form.serverDescription} onChange={(e) => set("serverDescription", e.target.value)} disabled={isLoading} rows={3} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Exigir assinatura de contrato dos clientes</p>
                  <p className="text-xs text-muted-foreground">Clientes precisam assinar o contrato antes de usar a plataforma.</p>
                </div>
                <Switch checked={form.requireClientContractSignature} onCheckedChange={(v) => set("requireClientContractSignature", v)} disabled={isLoading} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Notificar clientes sobre atualizações do sistema</p>
                  <p className="text-xs text-muted-foreground">
                    Mostra um aviso pedindo pra atualizar quando uma versão nova é publicada. Desligado, o app
                    atualiza sozinho em segundo plano sem avisar ninguém.
                  </p>
                </div>
                <Switch
                  checked={form.updateNotificationEnabled}
                  onCheckedChange={(v) => set("updateNotificationEnabled", v)}
                  disabled={isLoading}
                />
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="apple-url">Apple Store</Label>
                  <Input id="apple-url" value={form.appleStoreUrl} onChange={(e) => set("appleStoreUrl", e.target.value)} disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="google-url">Google Play</Label>
                  <Input id="google-url" value={form.googlePlayUrl} onChange={(e) => set("googlePlayUrl", e.target.value)} disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="manual-url">Manual do aplicativo</Label>
                  <Input id="manual-url" value={form.appManualUrl} onChange={(e) => set("appManualUrl", e.target.value)} disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="terms-url">Termos de uso</Label>
                  <Input id="terms-url" value={form.termsOfUseUrl} onChange={(e) => set("termsOfUseUrl", e.target.value)} disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="privacy-url">Política de privacidade</Label>
                  <Input id="privacy-url" value={form.privacyPolicyUrl} onChange={(e) => set("privacyPolicyUrl", e.target.value)} disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency-phone">Telefone de emergência</Label>
                  <Input id="emergency-phone" value={form.emergencyPhone} onChange={(e) => set("emergencyPhone", e.target.value)} disabled={isLoading} />
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="ig-url">Instagram</Label>
                  <Input id="ig-url" value={form.instagramUrl} onChange={(e) => set("instagramUrl", e.target.value)} disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fb-url">Facebook</Label>
                  <Input id="fb-url" value={form.facebookUrl} onChange={(e) => set("facebookUrl", e.target.value)} disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tw-url">Twitter/X</Label>
                  <Input id="tw-url" value={form.twitterUrl} onChange={(e) => set("twitterUrl", e.target.value)} disabled={isLoading} />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={update.isPending || isLoading}>
              {update.isPending ? "Salvando…" : "Salvar alterações"}
            </Button>
          </div>
        </div>

        <div className="lg:sticky lg:top-6 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pré-visualização</CardTitle>
              <CardDescription>Atualiza conforme você edita, antes mesmo de salvar.</CardDescription>
              <div className="mt-2 inline-flex w-fit rounded-lg border border-border p-1">
                <button
                  type="button"
                  onClick={() => setPreviewMode("web")}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    previewMode === "web" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  💻 Web
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMode("app")}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    previewMode === "app" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  📱 App
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {previewMode === "web" ? (
                <LaptopFrame>
                  <WebThemeMockup logoUrl={previewLogoUrl} primaryColor={form.primaryColor} dark={dark} />
                </LaptopFrame>
              ) : (
                <PhoneFrame>
                  <AppScreenMockup
                    logoUrl={previewLogoUrl}
                    primaryColor={form.primaryColor}
                    dark={dark}
                    appName={form.serverName || "Meu App"}
                  />
                </PhoneFrame>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function AppScreenMockup({
  logoUrl,
  primaryColor,
  dark,
  appName,
}: {
  logoUrl: string;
  primaryColor: string;
  dark: boolean;
  appName: string;
}) {
  return (
    <div className={cn(dark ? "bg-neutral-900" : "bg-neutral-100")}>
      <div className="flex items-center gap-2 px-3 py-2" style={{ backgroundColor: primaryColor }}>
        <img src={logoUrl} alt="Logotipo" className="h-5 w-5 rounded bg-white/90 object-contain" />
        <span className="truncate text-sm font-semibold text-white">{appName}</span>
      </div>
      <div className="h-72 space-y-2 p-3">
        <div className={cn("h-28 rounded-lg shadow-sm", dark ? "bg-neutral-800" : "bg-white")} />
        <div className={cn("h-5 w-2/3 rounded shadow-sm", dark ? "bg-neutral-800" : "bg-white")} />
        <div className={cn("h-5 w-1/2 rounded shadow-sm", dark ? "bg-neutral-800" : "bg-white")} />
        <div className="mt-2 inline-block rounded-full px-2 py-1 text-[10px] text-white" style={{ backgroundColor: primaryColor }}>
          Veículo 01
        </div>
      </div>
    </div>
  );
}

function LogoSlot({
  title,
  description,
  currentUrl,
  mutation,
  size,
  fill = false,
  darkPreview = false,
}: {
  title: string;
  description: string;
  currentUrl: string | null | undefined;
  mutation: UseMutationResult<UploadBrandingImageResult, unknown, File>;
  // Ausente pro favicon: quem decide o tamanho dele é o navegador (ícone da
  // aba), não tem um <img> nosso pra dimensionar — só topo/login têm isso.
  size?: { value: number; min: number; max: number; onChange: (value: number) => void };
  // Logo/favicon são ícones pequenos — object-contain centralizado num box
  // com fundo neutro é a prévia certa (mostra a imagem inteira). A imagem
  // de fundo da tela de login é usada como background-cover em tela cheia
  // (ver LoginBackgroundLayer) — mostrá-la do mesmo jeito faz parecer um
  // ícone perdido num quadro com "fundo do banner" sobrando ao redor
  // (relatado 2026-08-31). fill=true faz a prévia usar object-cover
  // preenchendo o box inteiro, do mesmo jeito que ela aparece de verdade.
  fill?: boolean;
  // Prévia em fundo escuro — pro logotipo do tema Escuro aparecer como vai ficar.
  darkPreview?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      await mutation.mutateAsync(file);
      toast.success(`${title} atualizado`);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : `Falha ao enviar ${title.toLowerCase()}`);
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
      <div
        className={cn(
          "flex items-center justify-center overflow-hidden rounded-lg border border-dashed border-border",
          darkPreview ? "bg-neutral-900" : "bg-muted/30",
          fill ? "h-28" : "h-20",
        )}
      >
        {currentUrl ? (
          <img
            src={resolveGpsDriveUrl(currentUrl)}
            alt={title}
            className={fill ? "h-full w-full object-cover" : "max-h-16 max-w-full object-contain"}
          />
        ) : (
          <span className="text-xs text-muted-foreground">Sem imagem</span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFile(e.target.files?.[0])}
        disabled={mutation.isPending}
        className="w-full text-xs file:mr-2 file:rounded-md file:border-0 file:bg-secondary file:px-2 file:py-1 file:text-xs file:font-medium file:text-secondary-foreground"
      />
      {mutation.isPending && <p className="text-xs text-muted-foreground">Enviando…</p>}
      {size && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Tamanho</Label>
            <span className="font-mono text-xs text-muted-foreground">{size.value}px</span>
          </div>
          <input
            type="range"
            min={size.min}
            max={size.max}
            value={size.value}
            onChange={(e) => size.onChange(Number(e.target.value))}
            className="w-full accent-primary"
            aria-label={`Tamanho de ${title.toLowerCase()}`}
          />
        </div>
      )}
    </div>
  );
}

// Mesma imagem do "Logotipo do topo" (nenhum upload próprio) — aqui só o
// tamanho é ajustável, porque o app/PWA usa o logotipo centralizado numa
// barra bem mais estreita que a sidebar, precisando de um tamanho próprio.
function AppLogoSizeSlot({
  logoUrl,
  value,
  min,
  max,
  onChange,
}: {
  logoUrl: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Logotipo do app (PWA)</p>
      <p className="text-xs text-muted-foreground">
        Barra superior da tela Início no app — mesma imagem do topo, tamanho próprio.
      </p>
      {/* Cresce com o valor (sem overflow-hidden/altura travada) — na tela
          real o logotipo virou `absolute`, então não empurra nada; aqui na
          prévia é o oposto: queremos mostrar o tamanho de verdade, não
          recortado numa caixa fixa. */}
      <div className="flex min-h-20 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 p-2">
        <img src={logoUrl} alt="Logotipo do app" style={{ height: value }} className="w-auto object-contain" />
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Tamanho</Label>
          <span className="font-mono text-xs text-muted-foreground">{value}px</span>
        </div>
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full accent-primary"
          aria-label="Tamanho do logotipo do app"
        />
      </div>
    </div>
  );
}
